import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
    Box, Typography, Card, Button, CircularProgress, Alert, IconButton, TextField, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { ArrowBack, Refresh, AddCircleOutlined as AddCircleOutline, PictureAsPdf, Psychology, Close, Download } from '@mui/icons-material';
import api from '../../utils/API';

import InterviewKitContent from './components/InterviewKitContent';

const EntretienCandidat = () => {
    const location = useLocation();
    const history = useHistory();
    const state = location.state || {};
    const { candidat, jobUuid, selectedMission } = state;

    const [loading, setLoading] = useState(false);
    const [regenerateLoading, setRegenerateLoading] = useState(false);
    const [addQuestionsLoading, setAddQuestionsLoading] = useState(false);
    const [interviewKit, setInterviewKit] = useState(null);
    const [pdfId, setPdfId] = useState(null);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [error, setError] = useState(null);
    const [numQuestionsToAdd, setNumQuestionsToAdd] = useState(3);
    const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);

    useEffect(() => {
        if (!candidat || !jobUuid) {
            setError("Les informations du candidat ou de la mission sont manquantes.");
            return;
        }
        generateInitialKit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidat, jobUuid]);

    const generateInitialKit = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.post('/mission/interview-kit', {
                candidate_uuid: candidat.id,
                job_uuid: jobUuid
            });
            const kitData = response.data?.kit || response.data?.data || response.data?.interview_kit || response.data?.questions || response.data;
            const extractedPdfId = response.data?.pdf_id || response.data?.file_id || response.data?.document_id || response.data?.pdf || response.data?.data?.pdf_id || response.data?.data?.file_id || response.data?.data?.pdf || response.data?.kit?.pdf_id;
            setInterviewKit(kitData);
            setPdfId(extractedPdfId || null);
        } catch (err) {

            setError("Impossible de générer l'entretien. Veuillez réessayer.");
        } finally {
            setLoading(false);
        }
    };

    const handleRegenerate = async () => {
        setRegenerateLoading(true);
        setError(null);
        try {
            let prevQs = [];
            if (interviewKit?.questions && Array.isArray(interviewKit.questions)) {
                prevQs = interviewKit.questions.map(q => typeof q === 'string' ? q : (q.question || q.title || q.name || ""));
            } else if (Array.isArray(interviewKit)) {
                prevQs = interviewKit.map(q => typeof q === 'string' ? q : (q.question || q.title || q.name || ""));
            }

            const response = await api.post('/mission/interview/regeneration', {
                candidate_uuid: candidat.id,
                job_uuid: jobUuid,
                previous_questions: prevQs
            });
            const kitData = response.data?.kit || response.data?.data || response.data?.interview_kit || response.data?.questions || response.data;
            const extractedPdfId = response.data?.pdf_id || response.data?.file_id || response.data?.document_id || response.data?.pdf || response.data?.data?.pdf_id || response.data?.data?.file_id || response.data?.data?.pdf || response.data?.kit?.pdf_id;
            setInterviewKit(kitData);
            setPdfId(extractedPdfId || null);
        } catch (err) {

            setError("Impossible de régénérer l'entretien.");
        } finally {
            setRegenerateLoading(false);
        }
    };

    const handleAddQuestions = async () => {
        setAddQuestionsLoading(true);
        setError(null);
        try {
            const response = await api.post('/mission/interview/add-questions', {
                candidate_uuid: candidat.id,
                job_uuid: jobUuid,
                existing_kit: interviewKit,
                num_additional_questions: numQuestionsToAdd
            });
            const kitData = response.data?.kit || response.data?.data || response.data?.interview_kit || response.data?.questions || response.data;
            const extractedPdfId = response.data?.pdf_id || response.data?.file_id || response.data?.document_id || response.data?.pdf || response.data?.data?.pdf_id || response.data?.data?.file_id || response.data?.data?.pdf || response.data?.kit?.pdf_id;
            setInterviewKit(kitData);
            setPdfId(extractedPdfId || null);
        } catch (err) {

            setError("Impossible d'ajouter des questions.");
        } finally {
            setAddQuestionsLoading(false);
        }
    };

    const exportToPDF = async () => {
        setDownloadingPdf(true);
        try {
            const response = await api.post('/mission/interview/kit/export/pdf', {
                candidate_uuid: candidat.id,
                job_uuid: jobUuid,
                kit: interviewKit
            }, {
                responseType: 'blob'
            });
            const url = URL.createObjectURL(response.data);
            setPdfPreviewUrl(url);
            setPreviewOpen(true);
        } catch (err) {

            setError("Impossible de générer l'aperçu PDF. Veuillez réessayer.");
        } finally {
            setDownloadingPdf(false);
        }
    };

    const handleDownloadPdf = () => {
        if (pdfPreviewUrl) {
            const link = document.createElement('a');
            link.href = pdfPreviewUrl;
            link.setAttribute('download', `Entretien_${fullName.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    };

    const handleClosePreview = () => {
        setPreviewOpen(false);
    };

    const p = candidat?.properties || {};
    const fullName = (p.full_name && p.full_name !== 'null') ? p.full_name : 'Candidat';

    return (
        <Box sx={{ px: 3, pt: 0, pb: 3, backgroundColor: '#f1f5f9', minHeight: '100vh', '@media print': { backgroundColor: '#fff', p: 0 } }}>
            <style>
                {`
                    @media print {
                        .no-print { display: none !important; }
                        body { background-color: #fff; }
                        .print-container { box-shadow: none !important; border: none !important; }
                    }
                `}
            </style>

            <Box className="no-print" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton aria-label="Retour" onClick={() => history.goBack()} sx={{ backgroundColor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', '&:hover': { backgroundColor: '#f8fafc' } }}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                    Entretien d'évaluation
                </Typography>
            </Box>

            {error && (
                <Alert className="no-print" severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                </Alert>
            )}

            {!candidat || !jobUuid ? null : (
                <Card className="print-container" sx={{ p: 4, borderRadius: 3, boxShadow: '0 10px 30px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0', backgroundColor: '#fff', maxWidth: 1000, margin: '0 auto' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, pb: 3, borderBottom: '1px solid #e2e8f0' }}>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Psychology sx={{ fontSize: 36, color: '#3b82f6' }} />
                                {fullName}
                            </Typography>
                            {selectedMission && (
                                <Typography variant="subtitle1" sx={{ color: '#64748b', fontWeight: 500 }}>
                                    Pour le poste : <span style={{ color: '#334155', fontWeight: 600 }}>{selectedMission.value}</span>
                                </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                                {p.years_of_experience > 0 && <Chip label={`${p.years_of_experience} ans d'exp`} size="small" sx={{ backgroundColor: '#f8fafc', color: '#475569' }} />}
                                {p.location && p.location !== 'not found' && <Chip label={p.location} size="small" sx={{ backgroundColor: '#f8fafc', color: '#475569' }} />}
                            </Box>
                        </Box>

                        <Box className="no-print" sx={{ display: 'flex', gap: 1.5, flexDirection: 'column', alignItems: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                startIcon={downloadingPdf ? <CircularProgress size={16} /> : <PictureAsPdf />}
                                onClick={exportToPDF}
                                disabled={downloadingPdf || loading}
                                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: '#0f172a', borderColor: '#cbd5e1' }}
                            >
                                {downloadingPdf ? 'Téléchargement...' : 'Exporter PDF'}
                            </Button>
                        </Box>
                    </Box>

                    {loading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
                            <CircularProgress sx={{ color: '#3b82f6', mb: 3 }} size={50} />
                            <Typography variant="h7" sx={{ color: '#475569', fontWeight: 500 }}>
                                Génération de l'entretien sur mesure...
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1 }}>
                                L'IA analyse le profil et les prérequis de la mission.
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            <Box className="no-print" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                <Button
                                    variant="outlined"
                                    startIcon={regenerateLoading ? <CircularProgress size={16} /> : <Refresh />}
                                    onClick={handleRegenerate}
                                    disabled={regenerateLoading || addQuestionsLoading || loading}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, color: '#3b82f6', borderColor: '#bfdbfe', backgroundColor: '#fff' }}
                                >
                                    Régénérer
                                </Button>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>Ajouter</Typography>
                                    <TextField
                                        size="small"
                                        type="number"
                                        value={numQuestionsToAdd}
                                        onChange={(e) => setNumQuestionsToAdd(parseInt(e.target.value) || 1)}
                                        sx={{ width: 60, backgroundColor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }} slotProps={{ htmlInput: { min: 1, max: 10 } }}
                                    />
                                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mr: 1 }}>questions</Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={addQuestionsLoading ? <CircularProgress size={16} color="inherit" /> : <AddCircleOutline />}
                                        onClick={handleAddQuestions}
                                        disabled={regenerateLoading || addQuestionsLoading || loading}
                                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, backgroundColor: '#3b82f6', '&:hover': { backgroundColor: '#2563eb' }, boxShadow: 'none' }}
                                    >
                                        Ajouter
                                    </Button>
                                </Box>
                            </Box>

                            <InterviewKitContent interviewKit={interviewKit} />
                        </Box>
                    )}
                </Card>
            )}
            <Dialog
                open={previewOpen}
                onClose={handleClosePreview}
                maxWidth="md"
                fullWidth
                slotProps={{ paper: { sx: { height: '90vh', borderRadius: 3 } } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0', p: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PictureAsPdf sx={{ color: '#ef4444' }} /> Aperçu de la fiche d'entretien
                    </Typography>
                    <IconButton aria-label="Fermer" onClick={handleClosePreview} size="small" sx={{ color: '#64748b' }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                    {pdfPreviewUrl && (
                        <iframe
                            src={`${pdfPreviewUrl}#navpanes=0`}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                            title="Aperçu PDF"
                        />
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                    <Button onClick={handleClosePreview} sx={{ color: '#475569', fontWeight: 600 }}>
                        Fermer
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleDownloadPdf}
                        startIcon={<Download />}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, backgroundColor: '#3b82f6', '&:hover': { backgroundColor: '#2563eb' }, boxShadow: 'none' }}
                    >
                        Télécharger le PDF
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
export default EntretienCandidat;